/* eslint-disable no-case-declarations */
import {
  CallingPeriodDetailsType,
  CallingPeriodValueType,
  CallingStatusDateRange,
} from '@/apis/callingstatusDateRange';
import { useDeferredRangeDateField } from '@/hooks';
import { isTemplatePlaceholder, TemplatePlaceholder } from '@/types';
import { AccordionField } from '@components/DrawerAccordion';
import ToolTipRadioWrapper from '@components/ToolTipRadioWrapper';
import {
  FormControl,
  FormHelperText,
  MenuItem,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Stack,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import TemplateCapableDatePicker from '@pages/AdvancedSearchListCreatorPage/customWidgets/templateCapableComponents/TemplateCapableDatePicker';
import TemplateCapableTextField from '@pages/AdvancedSearchListCreatorPage/customWidgets/templateCapableComponents/TemplateCapableTextField';
import { SearchTemplatePageContext } from '@/context/SearchTemplateContext';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

export type TemplatableCallingStatusDateRange = Omit<
  CallingStatusDateRange,
  'callingPeriodQuantity' | 'displayedStartDate' | 'displayedEndDate'
> & {
  callingPeriodQuantity: CallingStatusDateRange['callingPeriodQuantity'] | TemplatePlaceholder;
  displayedStartDate: CallingStatusDateRange['displayedStartDate'] | TemplatePlaceholder;
  displayedEndDate: CallingStatusDateRange['displayedEndDate'] | TemplatePlaceholder;
};

type CallingStatusDateRangePickerProps = {
  onChange: (newValue: TemplatableCallingStatusDateRange) => void;
  value: TemplatableCallingStatusDateRange;
  setIsFilterInvalid: (isInvalid: boolean) => void;
  isAdvancedSearch?: boolean;
};

type ErrorType = 'invalidDate' | 'invalidRange';

const ERROR_MESSAGE: Record<ErrorType, string> = {
  invalidDate: 'Please select a valid date',
  invalidRange: 'Please select a to date that is after the from date',
};

function computeCallingInvalidRange(val: TemplatableCallingStatusDateRange): boolean {
  const { displayedStartDate, displayedEndDate } = val;
  return Boolean(
    displayedStartDate &&
      displayedEndDate &&
      !isTemplatePlaceholder(displayedStartDate) &&
      !isTemplatePlaceholder(displayedEndDate) &&
      (displayedStartDate as Date) > (displayedEndDate as Date)
  );
}

const CallingStatusDateRangePicker = ({
  value,
  onChange,
  setIsFilterInvalid,
  isAdvancedSearch,
}: CallingStatusDateRangePickerProps) => {
  const isOnTemplatePage = useContext(SearchTemplatePageContext);
  const {
    displayedStartDate,
    displayedEndDate,
    callingPeriodValue,
    callingPeriodDetails,
    callingPeriodQuantity,
  } = value;
  const invalidRange = computeCallingInvalidRange(value);
  // to keep the error status consistent with DatePicker, we need to set the error status based on onError callback
  const [invalidFromDate, setInvalidFromDate] = useState(false);
  const [invalidToDate, setInvalidToDate] = useState(false);
  const invalidDate = invalidFromDate || invalidToDate;

  const invalidFromRef = useRef(false);
  const invalidToRef = useRef(false);
  // Keep period in a ref so deferred onError(null) from disabling the pickers
  // cannot clear sticky invalidity while Custom is inactive.
  const callingPeriodValueRef = useRef(callingPeriodValue);
  useEffect(() => {
    callingPeriodValueRef.current = callingPeriodValue;
  }, [callingPeriodValue]);

  const setInvalidFromForPicker = (next: boolean) => {
    invalidFromRef.current = next;
    setInvalidFromDate(next);
  };
  const setInvalidToForPicker = (next: boolean) => {
    invalidToRef.current = next;
    setInvalidToDate(next);
  };

  const reportValidity = (nextValue: TemplatableCallingStatusDateRange) => {
    const dateFieldsActive = nextValue.callingPeriodValue === 'custom';
    setIsFilterInvalid(
      (dateFieldsActive &&
        (invalidFromRef.current ||
          invalidToRef.current ||
          computeCallingInvalidRange(nextValue))) ||
        Number.isNaN(nextValue.callingPeriodQuantity)
    );
  };

  const reportFieldInvalidity = (setField: (next: boolean) => void, isInvalid: boolean) => {
    // Disabling the pickers (leaving Custom) can fire onError(null); ignore those
    // clears so returning to Custom still sees the sticky invalid flags.
    if (callingPeriodValueRef.current !== 'custom' && !isInvalid) {
      return;
    }
    setField(isInvalid);
    reportValidity({
      ...value,
      callingPeriodValue: callingPeriodValueRef.current,
    });
  };

  // Parent can replace `value` without firing our onChange (e.g. reducer / preset). Keep Apply disabled state in sync.
  /* eslint-disable react-you-might-not-need-an-effect/no-pass-data-to-parent -- keep Apply disabled when parent replaces value */
  useEffect(() => {
    const dateFieldsActive = value.callingPeriodValue === 'custom';
    setIsFilterInvalid(
      (dateFieldsActive &&
        (invalidFromRef.current ||
          invalidToRef.current ||
          computeCallingInvalidRange(value))) ||
        Number.isNaN(value.callingPeriodQuantity)
    );
  }, [value, setIsFilterInvalid]);
  /* eslint-enable react-you-might-not-need-an-effect/no-pass-data-to-parent */

  const dateFieldsActive = callingPeriodValue === 'custom';

  const errorMsg = useMemo(() => {
    if (!dateFieldsActive) return '';
    if (invalidDate) return ERROR_MESSAGE.invalidDate;
    if (invalidRange) return ERROR_MESSAGE.invalidRange;
    return '';
  }, [dateFieldsActive, invalidDate, invalidRange]);

  const changeHandler = ({
    type,
    payload,
  }: {
    type:
      | 'startDate'
      | 'endDate'
      | 'callingPeriodValue'
      | 'callingPeriodDetails'
      | 'callingPeriodQuantity';
    payload: unknown;
  }) => {
    switch (type) {
      case 'callingPeriodQuantity':
        onChange({
          ...value,
          callingPeriodQuantity: isTemplatePlaceholder(payload)
            ? (payload as TemplatePlaceholder)
            : (payload as number),
        });
        break;
      case 'startDate': {
        const next = {
          ...value,
          displayedStartDate: isTemplatePlaceholder(payload)
            ? (payload as TemplatePlaceholder)
            : (payload as Date | null),
        };
        onChange(next);
        reportValidity(next);
        break;
      }
      case 'endDate': {
        const next = {
          ...value,
          displayedEndDate: isTemplatePlaceholder(payload)
            ? (payload as TemplatePlaceholder)
            : (payload as Date | null),
        };
        onChange(next);
        reportValidity(next);
        break;
      }
      case 'callingPeriodValue': {
        const derivedPVPayload = payload as CallingPeriodValueType;
        const next = {
          ...value,
          callingPeriodValue: derivedPVPayload,
        };
        // Update before onChange so disable-driven onError(null) sees the new period.
        callingPeriodValueRef.current = derivedPVPayload;
        // Keep from/to invalid flags while away from Custom so returning to Custom
        // re-applies them. reportValidity ignores them when date fields are inactive.
        onChange(next);
        reportValidity(next);
        break;
      }
      case 'callingPeriodDetails':
        const derivedPDPayload = payload as CallingPeriodDetailsType;
        onChange({
          ...value,
          callingPeriodDetails: derivedPDPayload,
        });
        break;

      default:
        break;
    }
  };

  const fromField = useDeferredRangeDateField({
    value: displayedStartDate,
    reportInvalid: (isInvalid) => reportFieldInvalidity(setInvalidFromForPicker, isInvalid),
    commit: (newValue) => changeHandler({ type: 'startDate', payload: newValue }),
  });
  const toField = useDeferredRangeDateField({
    value: displayedEndDate,
    reportInvalid: (isInvalid) => reportFieldInvalidity(setInvalidToForPicker, isInvalid),
    commit: (newValue) => changeHandler({ type: 'endDate', payload: newValue }),
  });

  const handlePeriodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    changeHandler({
      type: 'callingPeriodValue',
      payload: (event.target as HTMLInputElement).value,
    });
  };

  const handleChangePeriodDetails = (event: SelectChangeEvent) => {
    changeHandler({ type: 'callingPeriodDetails', payload: event.target.value });
  };

  const { callingPeriodQuantityIsInvalid, callingPeriodQuantityTextFieldValue } =
    validateCallingPeriodQuantity(callingPeriodQuantity);

  const handleQuantityChange = (value: string | TemplatePlaceholder) => {
    if (isTemplatePlaceholder(value)) {
      changeHandler({ type: 'callingPeriodQuantity', payload: value });
    } else if (
      isTemplatePlaceholder(callingPeriodQuantityTextFieldValue) &&
      !isTemplatePlaceholder(value)
    ) {
      changeHandler({ type: 'callingPeriodQuantity', payload: 3 });
    } else {
      const quantityParsed = parseInt(value);
      if ((Number.isNaN(quantityParsed) && value === '') || quantityParsed >= 1) {
        changeHandler({ type: 'callingPeriodQuantity', payload: quantityParsed });
      }
    }
  };

  const disableRatioButtons =
    isTemplatePlaceholder(callingPeriodQuantityTextFieldValue) ||
    isTemplatePlaceholder(displayedStartDate) ||
    isTemplatePlaceholder(displayedEndDate);

  return (
    <AccordionField title="In the period:">
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{
            width: isAdvancedSearch ? '70ch' : '100%',
            minWidth: 0,
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            '& label': {
              paddingTop: '0.15rem',
            },
          }}
        >
          <FormControl>
            <RadioGroup
              aria-labelledby="engagement-period-radio-buttons-group"
              name="engagement-period-radio-buttons-group"
              value={callingPeriodValue}
              onChange={handlePeriodChange}
            >
              <ToolTipRadioWrapper
                value="withinlast"
                disabled={disableRatioButtons}
                label="Within last"
              />
              <ToolTipRadioWrapper value="custom" disabled={disableRatioButtons} label="Custom" />
            </RadioGroup>
          </FormControl>
          <Stack direction="row" useFlexGap sx={{ gap: '24px', flexShrink: 0 }}>
            <FormControl variant="filled">
              <TemplateCapableTextField
                value={callingPeriodQuantityTextFieldValue}
                onChange={handleQuantityChange}
                label="#*"
                placeholder=""
                inputMode="numeric"
                slotProps={{ htmlInput: { pattern: '[0-9]*' } }}
                type="text"
                sx={{ width: '70px', marginTop: '-5px' }}
                variant="filled"
                disabled={callingPeriodValue !== 'withinlast'}
                error={callingPeriodQuantityIsInvalid}
                isOnTemplatePage={isOnTemplatePage}
                templateBuilderDisplayName="Calling status filter: Within last"
                templateBuilderPlaceholderFieldLabel=""
              />
            </FormControl>
            <FormControl variant="filled">
              <Select
                value={callingPeriodDetails}
                onChange={handleChangePeriodDetails}
                sx={{ width: '130px', marginTop: '-5px' }}
                disabled={callingPeriodValue !== 'withinlast'}
              >
                <MenuItem value={'days'}>day(s)</MenuItem>
                <MenuItem value={'weeks'}>week(s)</MenuItem>
                <MenuItem value={'months'}>month(s)</MenuItem>
                <MenuItem value={'years'}>year(s)</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Stack>

        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          sx={{
            width: isAdvancedSearch ? '70ch' : '100%',
            minWidth: 0,
            justifyContent: 'space-between',
            pt: 2,
            'label:not(.Mui-focused)': {
              fontSize: '0.85rem',
              paddingTop: '0.15rem',
            },
          }}
        >
          <TemplateCapableDatePicker
            label="Contacted from date"
            sx={{ flex: 1, minWidth: 0 }}
            slotProps={{ textField: { fullWidth: true, ...fromField.textFieldSlotProps } }}
            views={['year', 'month', 'day']}
            value={fromField.displayValue}
            format="dd/MM/yyyy"
            onError={fromField.handleError}
            onChange={fromField.handleChange}
            isError={dateFieldsActive && (invalidRange || invalidFromDate)}
            disabled={!dateFieldsActive}
            isOnTemplatePage={isOnTemplatePage}
            templateBuilderDisplayName="Calling status filter: From date"
          />

          <TemplateCapableDatePicker
            label="Contacted to date"
            sx={{ flex: 1, minWidth: 0 }}
            slotProps={{ textField: { fullWidth: true, ...toField.textFieldSlotProps } }}
            views={['year', 'month', 'day']}
            value={toField.displayValue}
            format="dd/MM/yyyy"
            onError={toField.handleError}
            onChange={toField.handleChange}
            isError={dateFieldsActive && (invalidRange || invalidToDate)}
            disabled={!dateFieldsActive}
            isOnTemplatePage={isOnTemplatePage}
            templateBuilderDisplayName="Calling status filter: To date"
          />
        </Stack>
        {errorMsg && <FormHelperText className="Mui-error">{errorMsg}</FormHelperText>}
      </LocalizationProvider>
    </AccordionField>
  );
};

export default CallingStatusDateRangePicker;

function validateCallingPeriodQuantity(callingPeriodQuantity: number | TemplatePlaceholder) {
  let callingPeriodQuantityTextFieldValue, callingPeriodQuantityIsInvalid;
  if (isTemplatePlaceholder(callingPeriodQuantity)) {
    callingPeriodQuantityIsInvalid = false;
    callingPeriodQuantityTextFieldValue = callingPeriodQuantity;
  } else {
    callingPeriodQuantityIsInvalid = Number.isNaN(callingPeriodQuantity);
    callingPeriodQuantityTextFieldValue = callingPeriodQuantityIsInvalid
      ? ''
      : callingPeriodQuantity.toString();
  }

  return { callingPeriodQuantityTextFieldValue, callingPeriodQuantityIsInvalid };
}
